import { GetIssueResponseDto } from '@issuemanager/api-client';
import {
  IssueFieldValue,
  IssueStatusEnum,
  IssueTypeEnum,
} from '@issuemanager/constants';
import { useMemo } from 'react';

export interface UseManageIssueFieldStateParams {
  issue?: GetIssueResponseDto | null;
  userRoles?: {
    isCreator?: boolean;
    isIssueTypeManager?: boolean;
    isOwner?: boolean;
  } | null;
  fieldName: IssueFieldValue;
  isEditing: boolean;
}

export interface FieldState {
  editable: boolean;
  visible: boolean;
  required: boolean;
}

export const useManageIssueFieldState = ({
  issue,
  userRoles,
  fieldName,
  isEditing,
}: UseManageIssueFieldStateParams): FieldState =>
  useMemo(() => {
    if (!issue || !userRoles) {
      return { editable: false, visible: false, required: false };
    }

    const isDraft = issue.status === IssueStatusEnum.DRAFT;

    const calculateVisibility = (): boolean => {
      if (!issue.readableFields) return false;

      return issue.readableFields.includes(fieldName);
    };

    const calculateEditable = (): boolean => {
      if (!isEditing) return false;
      if (!issue.editableFields) return false;
      return issue.editableFields.includes(fieldName);
    };
    const calculateRequired = (): boolean => {
      const alwaysRequiredFields = ['title', 'description', 'severity'];

      if (alwaysRequiredFields.includes(fieldName)) {
        return true;
      }

      const requiredWhenNotDraft = ['owner', 'approver', 'dueDate'];

      if (!isDraft && requiredWhenNotDraft.includes(fieldName)) {
        return true;
      }

      const issueTypeName = issue.issueType?.name;
      const isAuditFinding = issueTypeName === IssueTypeEnum.AUDIT_FINDING;
      const isControlDeficiency =
        issueTypeName === IssueTypeEnum.CONTROL_DEFICIENCY;

      if (
        (isAuditFinding || isControlDeficiency) &&
        ['classification', 'rootCause'].includes(fieldName)
      ) {
        return true;
      }

      if (isAuditFinding && fieldName === 'recommendations') {
        return true;
      }

      return false;
    };

    return {
      visible: calculateVisibility(),
      editable: calculateEditable(),
      required: calculateRequired(),
    };
  }, [issue, userRoles, fieldName, isEditing]);
